/** @jsx isolateComponent */

import { css } from '@emotion/core';
import {
  Animation,
  Colors,
  Icon,
  Link,
  Typography,
  useTheme,
} from '@zapier/design-system';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import type { UniversalNavLinkType } from '../../../constants';

export type Props = {
  /**
   * Array of links to render as a submenu.
   */
  links: Array<UniversalNavLinkType>;
  /**
   * The toggle text of the submenu.
   */
  text: string;
};

const Styles = labelStyles('UniversalSidebarSubmenu', {
  summary: (isRebrand: boolean) => [
    css`
      display: block;
      border-radius: ${isRebrand ? '15px' : '10px'};
      color: ${isRebrand ? Colors.neutral700 : Colors.gray80};

      &:hover,
      &:focus {
        color: ${isRebrand ? Colors.neutral700 : Colors.gray};
        background-color: ${isRebrand ? Colors.cream : Colors.gray10};
        ${isRebrand && 'outline: solid 2px transparent;'}
      }
      ${isRebrand &&
      `&:focus {
        ${Typography.paragraph3Bold};
        outline: solid 2px ${Colors.brand};
      }
      outline: solid 2px transparent;
      transition: ${Animation.transitionValue};
      `}

      ${!isRebrand &&
      `[open] > & {
        color: ${Colors.ocean};
        background-color: ${Colors.ocean5};
      }`}
    `,
    css`
      /* Hides default icon on details element */
      &::-webkit-details-marker {
        display: none;
      }
      /* Hide default icon from IE. */
      &::before {
        display: none;
      }
    `,
  ],

  // Wrapper so layout works in Safari.
  summaryInner: (isRebrand: boolean) => css`
    ${isRebrand ? Typography.paragraph3Medium : Typography.base};
    ${!isRebrand && `font-size: 16px; font-weight: 600;`}
    line-height: 20px;
    cursor: pointer;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 20px;
    align-items: center;
    min-height: 50px;
    padding: 10px 15px;
    white-space: nowrap;
    width: 100%;
    transition: ${Animation.transitionValue};
  `,

  caret: css`
    transition: ${Animation.transitionValue};

    [open] & {
      transform: rotate(-180deg);
    }
  `,

  list: (isRebrand: boolean) => css`
    display: grid;
    grid-template-columns: 100%;
    gap: 5px;
    ${isRebrand ? `padding: 10px 0;` : `padding: 10px;`}
    ${!isRebrand && `border-bottom: 1px solid ${Colors.gray20};`}
  `,

  link: (isRebrand: boolean) => css`
    ${isRebrand ? Typography.paragraph3Medium : Typography.base};
    display: block;
    border-radius: ${isRebrand ? '5px' : '10px'};
    color: ${isRebrand ? Colors.neutral700 : Colors.gray80};
    line-height: 20px;
    ${!isRebrand && 'font-size: 14px;'}
    padding: 10px 15px;
    text-decoration: none;

    ${isRebrand
      ? `
    color: ${Colors.neutral700};
    outline: solid 2px transparent;
    transition: ${Animation.transitionValue};

    &:hover, &:focus {
      background-color: ${Colors.cream};
      outline: solid 2px transparent;
      text-decoration: none;
    }

    &:focus {
      ${Typography.paragraph3Bold};
      outline: solid 2px ${Colors.brand};
      text-decoration: none;
    }`
      : `
    &:hover, &:focus {
      color: ${Colors.gray};
      background-color: ${Colors.gray10};
      text-decoration: none;
    }
    `}
  `,

  linkInner: (isRebrand: boolean) => css`
    transform: translateY(1px);
    ${isRebrand && 'padding-left: 16px'}
  `,

  linkTitle: (isRebrand: boolean) => css`
    display: block;
    ${!isRebrand && 'font-weight: 600;'}
    text-decoration: none;
    outline: none;
  `,

  linkDescription: (isRebrand: boolean) => css`
    display: block;
    color: ${isRebrand ? Colors.neutral700 : Colors.gray80};
    margin-top: 5px;
    font-size: 12px;
    line-height: 15px;
    text-decoration: none;
    outline: none;
  `,
});

const SubmenuLink = (props: UniversalNavLinkType) => {
  const { isRebrand } = useTheme();

  return (
    <Link
      color={null}
      css={Styles.link(isRebrand)}
      href={props.href || undefined}
    >
      <div css={Styles.linkInner(isRebrand)}>
        <p css={Styles.linkTitle(isRebrand)}>{props.text}</p>
        {props.description && (
          <p css={Styles.linkDescription(isRebrand)}>{props.description}</p>
        )}
      </div>
    </Link>
  );
};

/**
 * @private
 * Renders a submenu within the universal sidebar.
 */
const UniversalSidebarSubmenu = (props: Props) => {
  const { isRebrand } = useTheme();

  const linkItems = props.links.map((link: UniversalNavLinkType) => {
    return (
      <li key={link.text}>
        <SubmenuLink {...link} />
      </li>
    );
  });

  return (
    <details>
      <summary css={Styles.summary(isRebrand)}>
        <div css={Styles.summaryInner(isRebrand)}>
          <span>{props.text}</span>
          <span css={Styles.caret}>
            <Icon isBlock={true} name="arrowSmallDown" size={24} />
          </span>
        </div>
      </summary>
      <ul css={Styles.list(isRebrand)}>{linkItems}</ul>
    </details>
  );
};

export default UniversalSidebarSubmenu;
